"use client"

import { Images } from "@/constants/images"
import { getFixedT } from "@/languages/i18n"
import { GoogleMap, InfoWindow, useJsApiLoader } from "@react-google-maps/api"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useMemo, useState } from "react"
import { TransformedStoreItem } from "../types"
import StoreMapSkeleton from "./StoreMapSkeleton"

interface StoreMapProps {
    stores: TransformedStoreItem[]
    selectedStore: TransformedStoreItem | null
    onStoreSelect?: (store: TransformedStoreItem) => void
    showMarkers?: boolean
    className?: string
    locale?: string
}

const StoreMap = ({ stores, selectedStore, onStoreSelect, showMarkers = false, className, locale }: StoreMapProps) => {
    const t = getFixedT(locale)
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "AIzaSyDGodfPRJzvat54G3EAHM87ymkPGL2HsvE",
    })

    const [map, setMap] = useState<google.maps.Map | null>(null)

    const defaultCenter = useMemo(() => {
        if (stores.length > 0 && stores[0].latitude && stores[0].longitude) {
            return {
                lat: stores[0].latitude,
                lng: stores[0].longitude,
            }
        }
        return {
            lat: 50.0755,
            lng: 14.4378,
        }
    }, [stores])

    const center = useMemo(() => {
        if (selectedStore && selectedStore.latitude && selectedStore.longitude) {
            return {
                lat: selectedStore.latitude,
                lng: selectedStore.longitude,
            }
        }
        return defaultCenter
    }, [selectedStore, defaultCenter])

    useEffect(() => {
        if (map && selectedStore && selectedStore.latitude && selectedStore.longitude) {
            map.panTo({
                lat: selectedStore.latitude,
                lng: selectedStore.longitude,
            })
            map.setZoom(9)
        }
    }, [map, selectedStore])

    if (!isLoaded) {
        return <StoreMapSkeleton />
    }

    return (
        <div className={`w-full ${className || "sm:w-[72.5%]"} border border-[#00000033] ${className ? "" : "border-l-0"} h-[400px] sm:h-auto relative z-10`}>
            <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={center}
                zoom={selectedStore ? 11 : 9}
                onLoad={(map) => {
                    setMap(map)
                    setTimeout(() => {
                        window.dispatchEvent(new Event("resize"))
                    }, 100)
                }}
                options={{
                    mapTypeControl: true,
                    mapTypeControlOptions: {
                        style: 1,
                        position: google.maps.ControlPosition.TOP_LEFT,
                        mapTypeIds: ["roadmap", "satellite"],
                    },
                    fullscreenControl: true,
                    fullscreenControlOptions: {
                        position: google.maps.ControlPosition.TOP_RIGHT,
                    },
                    zoomControl: true,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition.RIGHT_BOTTOM,
                    },
                    streetViewControl: false,
                }}
            >
                {stores.map((store) => {
                    if (!store.latitude || !store.longitude) return null

                    return (
                        <div
                            key={store.id}
                        >
                            {selectedStore?.id === store.id && (
                                <InfoWindow
                                    position={{
                                        lat: store.latitude,
                                        lng: store.longitude,
                                    }}
                                >
                                    <div className="w-[370px]">
                                        {/* Store Name and Button Row */}
                                        <div className="flex sm:flex-row flex-col sm:items-center sm:justify-between mb-2">
                                            <h3 className="text-[15px] leading-[18px] font-bold text-[#333] uppercase">{store.name}</h3>
                                            <Link
                                                href={`/stores/${store.id}`}
                                                className="text-sm leading-[30px] font-normal text-white px-[14px] bg-[#00a651] rounded btn-zoom-effect hover:text-white"
                                            >
                                                {t("Visit store")}
                                            </Link>
                                        </div>

                                        {/* Contact Information */}
                                        <div className="flex flex-col gap-2 py-3 border-y border-[#00000033]">
                                            {store.address && (
                                                <div className="flex items-start gap-1">
                                                    <div className="relative w-[14px] h-3"><Image src={Images.iconLocationStore} fill alt="icon-location" className="w-full h-full object-contain" /></div>
                                                    <span className="text-[13px] leading-[15px] text-[#333]">{store.address}</span>
                                                </div>
                                            )}

                                            <div className="flex sm:items-center sm:gap-6 flex-col sm:flex-row gap-2">
                                                {store.email && (
                                                    <div className="flex items-center gap-1">
                                                        <div className="relative w-[14px] h-3"><Image src={Images.iconEmail} fill alt="icon-mail" className="w-full h-full object-contain" /></div>
                                                        <Link
                                                            href={`mailto:${store.email}`}
                                                            className="text-[13px] leading-[15px] text-[#333] hover:text-[#00A651]"
                                                        >
                                                            {store.email}
                                                        </Link>
                                                    </div>
                                                )}

                                                {store.phone && (
                                                    <div className="flex items-center gap-1">
                                                        <div className="relative w-[14px] h-3"><Image src={Images.iconPhone} fill alt="icon-phone" className="w-full h-full object-contain" /></div>
                                                        <Link
                                                            href={`tel:${store.phone.replace(/\s+/g, "")}`}
                                                            className="text-[13px] leading-[15px] text-[#333] hover:text-[#00A651]"
                                                        >
                                                            {store.phone}
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Opening Hours */}
                                        {store.openingHours && store.openingHours.length > 0 && (
                                            <div className="py-3">
                                                <div className="grid grid-cols-2 gap-x-1">
                                                    {store.openingHours.map((item, idx) => (
                                                        <div key={idx} className="flex justify-between">
                                                            <span className="text-xs leading-[14px] text-[#333]">{t(item.day)}</span>
                                                            <span className="text-xs leading-[14px] text-[#333] font-semibold">{item.hours}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </InfoWindow>
                            )}
                        </div>
                    )
                })}
            </GoogleMap>
        </div>
    )
}

export default StoreMap
